import { Link, Outlet, useParams } from "@tanstack/react-router";

import { useFaqSummary, useSite } from "../../api/queries";
import { useCurrentOrganization } from "../../auth/session";
import { ErrorNote, Loading } from "../../ui/primitives";
import { StatusBadge } from "../SitesPage";

const TABS = [
  { to: "/sites/$siteId", label: "Overview", exact: true },
  { to: "/sites/$siteId/content", label: "Content" },
  { to: "/sites/$siteId/conversations", label: "Conversations" },
  { to: "/sites/$siteId/questions", label: "Q&A" },
  { to: "/sites/$siteId/analytics", label: "Analytics" },
  { to: "/sites/$siteId/widget", label: "Widget" },
  { to: "/sites/$siteId/connect", label: "Install" },
] as const;

export function useSiteContext() {
  const { siteId } = useParams({ from: "/app/sites/$siteId" });
  const organization = useCurrentOrganization();
  return { orgId: organization?.organization.id ?? "", siteId };
}

export function SiteLayout() {
  const { orgId, siteId } = useSiteContext();
  const site = useSite(orgId, siteId);
  // Drives the count beside the Q&A tab. An unanswered question is the
  // one thing on this screen that is costing the store traffic right
  // now, so it is worth surfacing without asking anyone to go looking.
  const faq = useFaqSummary(orgId, siteId);

  if (site.isLoading) return <Loading />;
  if (site.error) return <ErrorNote error={site.error} />;
  if (!site.data) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">{site.data.name}</h1>
        <StatusBadge status={site.data.status} />
        <a
          href={site.data.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-muted underline"
        >
          {site.data.url}
        </a>
      </div>

      {/* A tablist of links rather than ARIA tabs: each one is a real URL a
          customer can bookmark and share with support, which is worth more
          here than the arrow-key behaviour a tab widget would add. */}
      <nav aria-label="Site sections" className="flex gap-1 overflow-x-auto border-b border-line">
        {TABS.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            params={{ siteId }}
            activeOptions={{ exact: "exact" in tab && tab.exact }}
            className="whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-sm text-muted hover:text-ink"
            activeProps={{
              className:
                "whitespace-nowrap border-b-2 border-brand px-3 py-2 text-sm font-medium text-ink",
            }}
          >
            {tab.label}
            {tab.label === "Q&A" && (faq.data?.pending ?? 0) > 0 && (
              <span className="ml-1.5 rounded-full bg-warn/20 px-1.5 py-0.5 text-xs font-medium tabular-nums text-ink">
                {faq.data?.pending}
              </span>
            )}
          </Link>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
